// @flow
import type { Action, CartData } from '../actions/types';
import {
	UPDATE_CART_SUCCESS,
	TOGGLE_RESET_CART,
	ADD_LAST_TRANSACTION,
	CLEAR_LAST_TRANSACTION,
	RESET_CART,
	// SAVE_CART,
	INITIATE_SAVE_CART,
	SAVE_CART_SUCCESS,
	FETCH_CART,
	FETCH_CART_SUCCESS,
	FETCH_CART_ERROR,
	SET_CART_ORDER_ID,
	SET_TRANSACTION_VALUE,
	SAVE_CART_ERROR,
} from '../actions/types';
// import { SAMPLING_KEY } from '../actions/types';

export const MACHINE_SKUS = '1';
export const VIRTUAL_SKUS = '2';

export type SKUS_TYPE = '1' | '2';

export type CartSku = {
	totalQty: number,
	totalAmount: number,
	totalBillValue: number,
	skus: Array<any>,
};
type CartSkus = {
	[SKUS_TYPE]: CartSku,
};

export type CartState = {|
	sku_json: CartSkus,
	totalQty: number,
	totalAmount: number,
	totalBillValue: number,
	offerApplied: boolean,
	offerAmount: number,
	imei: string,
	machine: any | null,
	error: any,
	resetCartDlg: {
		open: boolean,
		type?: 'ACTIVE_ORDER' | 'CART',
	},
	lastTransaction: CartData | null,
	fetched: boolean,
	isServer: boolean,
	lastUpdated: number,
	cartError: boolean,
	orderId: string,
	savingCart: boolean,
	totalTransactionValue: number,
	cbAmount: number,
	cbDesc: string,
	message: string,
	delivery_discounted_fee: number,
	delivery_list_fee: number,
|};

export type SaveTrigger = 'PRODUCTS' | 'CART';

export const initialState: CartState = {
	sku_json: {},
	totalQty: 0,
	totalAmount: 0,
	totalBillValue: 0,
	offerApplied: false,
	offerAmount: 0,
	error: null,
	imei: '',
	machine: null,
	resetCartDlg: {
		open: false,
	},
	lastTransaction: null,
	fetched: false,
	isServer: false,
	lastUpdated: 0,
	cartError: false,
	orderId: '',
	savingCart: false,
	totalTransactionValue: 0, // added for cart value (+ add wallet amount or pending balance)
	cbAmount: 0,
	cbDesc: '',
	message: '',
	delivery_discounted_fee: 0,
	delivery_list_fee: 0,
};

export const cartReducer = (state: CartState = initialState, { type, payload }: Action): CartState => {
	switch (type) {
		case UPDATE_CART_SUCCESS:
			return {
				...state,
				...processUpdateCart(payload, state),
			};

		case TOGGLE_RESET_CART:
			return {
				...state,
				resetCartDlg: payload,
			};

		case ADD_LAST_TRANSACTION:
			return {
				...state,
				lastTransaction: payload,
			};

		case CLEAR_LAST_TRANSACTION:
			return {
				...state,
				lastTransaction: null,
			};

		case RESET_CART:
			return {
				...initialState,
			};

		case FETCH_CART:
			return {
				...state,
				error: null,
				fetched: false,
			};

		// case SAVE_CART:
		//   return {
		//     ...state,
		//     isServer: false,
		//   };

		case INITIATE_SAVE_CART:
			return {
				...state,
				error: null,
				isServer: false,
				savingCart: true,
			};

		case FETCH_CART_SUCCESS:
		case SAVE_CART_SUCCESS:
			return {
				...state,
				...payload,
				error: null,
			};

		case FETCH_CART_ERROR:
			return {
				...state,
				fetched: true,
				error: payload,
			};
		case SAVE_CART_ERROR:
			return {
				...state,
				isServer: false,
				savingCart: false,
				error: payload,
			};

		case SET_CART_ORDER_ID:
			return {
				...state,
				orderId: payload,
			};

		case SET_TRANSACTION_VALUE:
			return {
				...state,
				totalTransactionValue: payload,
			};

		default:
			return state;
	}
};

const processUpdateCart = (cartData: CartData = {}, state: CartState): CartState => {
	const { skuData: { sku_id, key, mrp, isVirtualSku } = {}, isSampling, action, qty, imei, machine } = cartData;
	let { amount } = cartData;
	if (!sku_id) {
		return state;
	}

	let { sku_json = {}, error, totalQty, totalAmount, totalBillValue } = state;
	let {
		skus = [],
		totalQty: skusTotalQty = 0,
		totalAmount: skusTotalAmount = 0,
		totalBillValue: skusTotalBillValue = 0,
	} = sku_json[isVirtualSku ? VIRTUAL_SKUS : MACHINE_SKUS] || {};

	let cartSkuIndex = skus.findIndex(
		(sku) => sku.key === key && ((isSampling && sku.is_sample) || (!isSampling && !sku.is_sample))
	);

	if (action === 'ADD') {
		if (cartSkuIndex < 0) {
			skus = [
				...skus,
				{
					...cartData.skuData,
					amount,
					purchased_qty: qty,
					is_sample: isSampling,
					totalMRP: +mrp,
				},
			];
		} else {
			skus[cartSkuIndex] = {
				...skus[cartSkuIndex],
				purchased_qty: skus[cartSkuIndex].purchased_qty + qty,
				amount: skus[cartSkuIndex].amount + amount,
				totalMRP: skus[cartSkuIndex].totalMRP + +mrp,
			};
			skus = [...skus];
		}

		// for machine skus
		skusTotalQty += qty;
		skusTotalAmount += amount;
		skusTotalBillValue += +mrp;

		// for combine skus
		totalQty += qty;
		totalAmount += amount;
		totalBillValue += +mrp;
	} else if (action === 'REMOVE') {
		if (cartSkuIndex < 0 && !isSampling) {
			// if item not exist then remove sampling data
			cartSkuIndex = skus.findIndex((sku) => sku.sku_id === sku_id);
			const cartSku = skus[cartSkuIndex];
			amount = cartSku ? cartSku['amount'] / cartSku['purchased_qty'] : 0; // sampling price is currently 1
		}

		if (cartSkuIndex < 0) {
			error = new Error('Item does not exist in the cart');
		} else {
			const cartSku = skus[cartSkuIndex];
			if (cartSku.purchased_qty - qty <= 0) {
				if (isSampling) {
					const nonSampleSkuIndex = skus.findIndex((sku) => sku.key === key && !sku.is_sample);
					if (nonSampleSkuIndex >= 0) {
						const nonSampleSku = skus.splice(nonSampleSkuIndex, 1);
						skus = [...skus];
						amount = nonSampleSku[0]['amount'] / nonSampleSku[0]['purchased_qty'];
					} else {
						skus.splice(cartSkuIndex, 1);
						skus = [...skus];
					}
				} else {
					skus.splice(cartSkuIndex, 1);
					skus = [...skus];
				}
				// skus.splice(cartSkuIndex, 1);
				// skus = [...skus];
			} else {
				const totAmt = cartSku.amount - amount;
				skus[cartSkuIndex] = {
					...skus[cartSkuIndex],
					purchased_qty: cartSku.purchased_qty - qty,
					amount: totAmt > 0 ? totAmt : 0,
					totalMRP: skus[cartSkuIndex].totalMRP - +mrp,
				};
			}
		}

		// for machine skus
		skusTotalQty -= qty;
		skusTotalAmount -= amount;
		skusTotalBillValue -= +mrp;
		if (skusTotalAmount <= 0 || skusTotalQty <= 0) {
			skus = [];
			skusTotalAmount = 0;
			skusTotalQty = 0;
			skusTotalBillValue = 0;
		}

		// For combine skus
		totalQty -= qty;
		totalAmount -= amount;
		totalBillValue -= +mrp;
		if (totalAmount <= 0 || totalQty <= 0) {
			skus = [];
			skusTotalAmount = 0;
			skusTotalQty = 0;
			skusTotalBillValue = 0;
		}
	}

	sku_json = ({...sku_json}: CartSkus);
	sku_json[isVirtualSku ? VIRTUAL_SKUS : MACHINE_SKUS] = {
		totalQty: skusTotalQty,
		totalAmount: skusTotalAmount,
		totalBillValue: skusTotalBillValue,
		skus: [...skus],
	};

	return {
		...state,
		sku_json,
		totalQty,
		totalBillValue,
		totalAmount,
		error,
		imei,
		machine,
	};
};
